import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import {
  CheckCircle,
  XCircle,
  UploadCloud,
  FileText,
  Trash2,
  Plus,
  ArrowLeft,
  ArrowRight,
  Check,
  Image as ImageIcon,
} from 'lucide-react';
import { apiClient as api, getProjects, Project } from '../../services/api';
import VideoUploader from './VideoUploader';
import WhitepaperPanel from './WhitepaperPanel';
import './Products.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


// ---- Interfaces ----

interface ProductVariant {
  variant_id?: string;
  label: string;
  price: string; // string for form control
  currency: string;
  billing_type: string;
  stock_quantity: string; // blank = unlimited
  stock_sold?: number;    // read-only, from server
}

interface UploadedFile {
  file_id?: string;
  name: string;
  size: number;
  type: string;
  localFile?: File; // only before upload
  uploaded?: boolean;
}

interface DetailsForm {
  name: string;
  category: string;
  description: string;
  short_description: string;
  product_type: string;
  is_lead_magnet: boolean;
  /** Drives which project's custom domain (e.g. pages.goodformco.au) a
   *  product's share link is branded under -- see _product_share_base_url
   *  in product_routes.py. Empty string = unlinked (falls back to
   *  aime.clearbyte.au). Auto-defaults to the active project below. */
  project_id: string;
}

const STEPS = ['Details', 'Files', 'Pricing', 'Publish'];

const EMPTY_DETAILS: DetailsForm = {
  name: '',
  category: 'ebook',
  description: '',
  short_description: '',
  product_type: 'download',
  is_lead_magnet: false,
  project_id: '',
};

const EMPTY_VARIANT: ProductVariant = {
  label: '',
  price: '',
  currency: 'USD',
  billing_type: 'one_time',
  stock_quantity: '',
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileExt = (name: string): string => {
  const parts = name.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'FILE';
};

// ---- Component ----

const ProductEditor: React.FC = () => {
  const navigate = useNavigate();
  const { productId } = useParams<{ productId: string }>();
  const isEditing = Boolean(productId);
  const { selectedProjectId } = useOutletContext<{ selectedProjectId?: string }>() || {};

  const [currentStep, setCurrentStep] = useState(0);
  const [savedProductId, setSavedProductId] = useState<string | null>(productId || null);

  // Step 1 state
  const [details, setDetails] = useState<DetailsForm>(EMPTY_DETAILS);
  const [detailsError, setDetailsError] = useState('');
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);

  // Auto-detect and assign the active project (once Layout's outlet context
  // is available) -- for a brand-new product, and for an existing one that
  // was created before this field existed / was never linked. Only fills
  // the default while project_id is still empty; never overwrites a value
  // the product already has (from the server, or a deliberate user choice
  // made after this effect last ran).
  useEffect(() => {
    if (selectedProjectId) {
      setDetails(prev => (prev.project_id ? prev : { ...prev, project_id: selectedProjectId }));
    }
  }, [selectedProjectId]);

  useEffect(() => {
    getProjects().then(setProjects).catch(() => setProjects([]));
  }, []);

  // Step 2 state
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [filesError, setFilesError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step 3 state
  const [variants, setVariants] = useState<ProductVariant[]>([{ ...EMPTY_VARIANT }]);
  const [variantsError, setVariantsError] = useState('');
  const [variantsSaving, setVariantsSaving] = useState(false);

  // Cover image state
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverError, setCoverError] = useState('');
  const coverInputRef = useRef<HTMLInputElement>(null);

  // Step 4 state
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState('');
  const [isLive, setIsLive] = useState(false);
  const [gatewayConfigured, setGatewayConfigured] = useState<boolean | null>(null);

  // Whitepaper metadata — set only for whitepaper-backed products
  // (product.source_task_id != null). Not part of the 4-step wizard: shown
  // as its own panel whenever an existing whitepaper product is opened.
  const [sourceTaskId, setSourceTaskId] = useState<string | null>(null);
  const [whitepaperGated, setWhitepaperGated] = useState(true);
  const [productStatus, setProductStatus] = useState('draft');

  // Load existing product
  useEffect(() => {
    if (productId) {
      loadProduct(productId);
    }
    checkGateway();
  }, [productId]);

  const loadProduct = async (id: string) => {
    try {
      const res = await api.get(`/products/${id}`);
      const p = res.data;
      setDetails({
        name: p.name || '',
        category: p.category || 'ebook',
        description: p.description || '',
        short_description: p.short_description || '',
        product_type: p.product_type || 'download',
        is_lead_magnet: Boolean(p.is_lead_magnet),
        project_id: p.project_id || '',
      });
      if (p.files) {
        setFiles(
          p.files.map((f: any) => ({
            file_id: f.file_id,
            name: f.name || f.filename,
            size: f.size || 0,
            type: f.content_type || '',
            uploaded: true,
          }))
        );
      }
      if (p.variants && p.variants.length > 0) {
        setVariants(
          p.variants.map((v: any) => ({
            variant_id: v.variant_id,
            label: v.label || '',
            price: String(v.price ?? ''),
            currency: v.currency || 'USD',
            billing_type: v.billing_type || 'one_time',
            stock_quantity: v.stock_quantity != null ? String(v.stock_quantity) : '',
            stock_sold: v.stock_sold ?? 0,
          }))
        );
      }
      setIsLive(p.status === 'active');
      setProductStatus(p.status || 'draft');
      setSourceTaskId(p.source_task_id || null);
      setWhitepaperGated(p.whitepaper_gated !== false);
      if (p.cover_image_url) setCoverUrl(p.cover_image_url);
    } catch {
      setDetailsError('Failed to load product.');
    }
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !savedProductId) return;
    if (file.size > 5 * 1024 * 1024) {
      setCoverError('Image must be under 5 MB.');
      return;
    }
    setCoverError('');
    setCoverUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/products/${savedProductId}/cover`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCoverUrl(res.data.cover_image_url + '?t=' + Date.now());
    } catch (err: any) {
      setCoverError(getApiError(err, 'Upload failed. Please try again.'));
    } finally {
      setCoverUploading(false);
      e.target.value = '';
    }
  };

  const handleCoverRemove = async () => {
    if (!savedProductId || !coverUrl) return;
    try {
      await api.delete(`/products/${savedProductId}/cover`);
      setCoverUrl(null);
    } catch {
      // silent — cover just won't show
    }
  };

  const checkGateway = async () => {
    try {
      const res = await api.get('/products/settings/gateways');
      const gateways: any[] = res.data.gateways || res.data;
      setGatewayConfigured(gateways.some((g: any) => g.verified));
    } catch {
      setGatewayConfigured(false);
    }
  };

  // ---- Step 1: Save Details ----
  const saveDetails = async (): Promise<string | null> => {
    if (!details.name.trim()) {
      setDetailsError('Product name is required.');
      return null;
    }
    setDetailsSaving(true);
    setDetailsError('');
    // project_id is nullable server-side; send null rather than '' so an
    // unlinked product stores NULL, not an empty string.
    const payload = { ...details, project_id: details.project_id || null };
    try {
      if (savedProductId) {
        await api.patch(`/products/${savedProductId}`, payload);
        return savedProductId;
      } else {
        const res = await api.post('/products/', payload);
        const newId = res.data.product_id;
        setSavedProductId(newId);
        return newId;
      }
    } catch (err: any) {
      setDetailsError(getApiError(err, 'Failed to save product details.'));
      return null;
    } finally {
      setDetailsSaving(false);
    }
  };

  // ---- Step 2: File Upload ----
  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addLocalFiles(droppedFiles);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addLocalFiles(Array.from(e.target.files));
      e.target.value = '';
    }
  };

  const addLocalFiles = (newFiles: File[]) => {
    const mapped: UploadedFile[] = newFiles.map(f => ({
      name: f.name,
      size: f.size,
      type: f.type,
      localFile: f,
      uploaded: false,
    }));
    setFiles(prev => [...prev, ...mapped]);
  };

  const removeFile = async (index: number) => {
    const file = files[index];
    if (file.file_id && savedProductId) {
      try {
        await api.delete(`/products/${savedProductId}/files/${file.file_id}`);
      } catch {
        // non-fatal
      }
    }
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadPendingFiles = async (pid: string) => {
    const pending = files.filter(f => !f.uploaded && f.localFile);
    if (pending.length === 0) return;

    setUploading(true);
    setFilesError('');
    try {
      for (const f of pending) {
        const formData = new FormData();
        formData.append('file', f.localFile!);
        const res = await api.post(`/products/${pid}/files`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setFiles(prev =>
          prev.map(pf =>
            pf.name === f.name && !pf.uploaded
              ? { ...pf, file_id: res.data.file_id, uploaded: true, localFile: undefined }
              : pf
          )
        );
      }
    } catch (err: any) {
      setFilesError(getApiError(err, 'File upload failed.'));
    } finally {
      setUploading(false);
    }
  };

  // ---- Step 3: Variants ----
  const addVariant = () => {
    setVariants(prev => [...prev, { ...EMPTY_VARIANT }]);
  };

  const updateVariant = (index: number, field: keyof ProductVariant, value: string) => {
    setVariants(prev =>
      prev.map((v, i) => (i === index ? { ...v, [field]: value } : v))
    );
  };

  const removeVariant = async (index: number) => {
    const v = variants[index];
    if (v.variant_id && savedProductId) {
      try {
        await api.delete(`/products/${savedProductId}/variants/${v.variant_id}`);
      } catch {
        // non-fatal
      }
    }
    setVariants(prev => prev.filter((_, i) => i !== index));
  };

  const saveVariants = async (pid: string) => {
    setVariantsSaving(true);
    setVariantsError('');
    try {
      for (const v of variants) {
        const stockQty = v.stock_quantity.trim() === '' ? null : parseInt(v.stock_quantity, 10);
        if (v.variant_id) {
          // Existing variant — only update stock_quantity (price/label locked after publish)
          await api.patch(`/products/${pid}/variants/${v.variant_id}`, {
            stock_quantity: stockQty ?? -1, // -1 signals "remove limit"
          });
        } else {
          if (!v.label && !v.price) continue; // skip empty rows
          await api.post(`/products/${pid}/variants`, {
            label: v.label,
            price: parseFloat(v.price) || 0,
            currency: v.currency,
            billing_type: v.billing_type,
            ...(stockQty !== null ? { stock_quantity: stockQty } : {}),
          });
        }
      }
    } catch (err: any) {
      setVariantsError(getApiError(err, 'Failed to save pricing variants.'));
      throw err;
    } finally {
      setVariantsSaving(false);
    }
  };

  // ---- Navigation ----
  const handleNext = async () => {
    if (currentStep === 0) {
      const pid = await saveDetails();
      if (!pid) return;
      setCurrentStep(1);
    } else if (currentStep === 1) {
      if (!savedProductId) { setCurrentStep(2); return; }
      await uploadPendingFiles(savedProductId);
      if (!filesError) setCurrentStep(2);
    } else if (currentStep === 2) {
      if (!savedProductId) { setCurrentStep(3); return; }
      try {
        await saveVariants(savedProductId);
        setCurrentStep(3);
      } catch {
        // error already set
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  // ---- Publish ----
  const handlePublish = async () => {
    if (!savedProductId) return;
    setPublishing(true);
    setPublishError('');
    try {
      await api.post(`/products/${savedProductId}/publish`);
      setIsLive(true);
      // IMPR-N4: WhitepaperPanel's draft-status warning banner is driven by
      // `productStatus`, not `isLive` -- these used to be two separately
      // tracked values that only agreed on initial load. Publishing here
      // updated isLive but left productStatus at its stale 'draft' value,
      // so the banner kept showing "still draft" for an already-live
      // product -- actively wrong, worse than no banner. Backend sets
      // status='active' on publish (product_routes.publish_product); mirror
      // it here.
      setProductStatus('active');
    } catch (err: any) {
      setPublishError(getApiError(err, 'Failed to publish product.'));
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!savedProductId) return;
    try {
      await api.post(`/products/${savedProductId}/unpublish`);
      setIsLive(false);
      // IMPR-N4: mirror unpublish_product's status='draft' (see handlePublish).
      setProductStatus('draft');
    } catch (err: any) {
      setPublishError(getApiError(err, 'Failed to unpublish product.'));
    }
  };

  // ---- Checklist ----
  const isPhysical = details.product_type === 'physical';
  const isVideo = details.product_type === 'video';
  const hasName = Boolean(details.name.trim());
  const hasFile = isPhysical || isVideo || files.filter(f => f.uploaded || f.localFile).length > 0;
  const priceableVariants = variants.filter(v => v.price && !isNaN(Number(v.price)));
  const hasVariant = priceableVariants.length > 0;
  // A product whose priceable variants are all $0 needs no payment gateway —
  // it's delivered through the free-checkout path (product_routes.py's
  // checkout()/quick_buy() $0 bypass), which never touches Stripe.
  const needsGateway = priceableVariants.some(v => Number(v.price) > 0);
  const publishReady = hasName && hasFile && hasVariant && (!needsGateway || gatewayConfigured === true);

  const checklistItems = [
    { label: 'Product details complete (name filled)', pass: hasName },
    ...(!isPhysical && !isVideo ? [{ label: 'At least one file uploaded', pass: hasFile }] : []),
    ...(isVideo ? [{ label: 'Video upload (optional — can be added after publishing)', pass: true }] : []),
    { label: 'At least one pricing variant set', pass: hasVariant },
    ...(needsGateway ? [{
      label: 'Payment gateway configured',
      pass: gatewayConfigured === true,
      hint: gatewayConfigured === false ? 'Configure in Settings tab' : undefined,
    }] : [{ label: 'Payment gateway (not required — all variants are free)', pass: true }]),
  ];

  // ---- Render ----
  return (
    <div className="product-editor">
      <div className="product-editor-header">
        <GlassButton
          variant="ghost"
          size="sm"
          icon={<ArrowLeft size={16} />}
          onClick={() => navigate('/products')}
          title="Back to Products"
        />
        <h1>{isEditing ? 'Edit Product' : 'New Product'}</h1>
      </div>

      {/* Whitepaper panel — only for products generated from an article */}
      {isEditing && savedProductId && sourceTaskId && (
        <GlassCard variant="default" padding="lg" className="editor-step-panel">
          <WhitepaperPanel
            productId={savedProductId}
            initialGated={whitepaperGated}
            productStatus={productStatus}
            onGatedChange={setWhitepaperGated}
          />
        </GlassCard>
      )}

      {/* Step Indicator */}
      <div className="step-indicator">
        {STEPS.map((step, index) => {
          const isActive = index === currentStep;
          const isComplete = index < currentStep;
          return (
            <React.Fragment key={step}>
              <div className="step-item">
                <div className="step-dot-wrapper">
                  <div className={`step-dot${isActive ? ' active' : ''}${isComplete ? ' complete' : ''}`}>
                    {isComplete ? <Check size={14} /> : index + 1}
                  </div>
                  <span className={`step-label${isActive ? ' active' : ''}${isComplete ? ' complete' : ''}`}>
                    {step}
                  </span>
                </div>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`step-connector${isComplete ? ' complete' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* ---- Step 0: Details ---- */}
      {currentStep === 0 && (
        <GlassCard variant="default" padding="lg" className="editor-step-panel">
          <h2>Product Details</h2>

          {detailsError && <div className="products-error">{detailsError}</div>}

          <div className="editor-form-group">
            <label>Product Name *</label>
            <input
              type="text"
              placeholder="e.g. Ultimate SEO Toolkit"
              value={details.name}
              onChange={e => setDetails(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>

          <div className="editor-form-group">
            <label>Category</label>
            <select
              value={details.category}
              onChange={e => setDetails(prev => ({ ...prev, category: e.target.value }))}
            >
              <option value="ebook">eBook</option>
              <option value="template">Template</option>
              <option value="software">Software</option>
              <option value="course">Course</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="editor-form-group">
            <label>Product Type</label>
            <select
              value={details.product_type}
              onChange={e => setDetails(prev => ({ ...prev, product_type: e.target.value }))}
            >
              <option value="download">Digital Download</option>
              <option value="physical">Physical Product</option>
              <option value="video">Video Course / Stream</option>
              <option value="access">Access / Membership</option>
              <option value="lead_magnet">Lead Magnet (Free)</option>
            </select>
          </div>

          <div className="editor-form-group">
            <label>Project</label>
            <select
              value={details.project_id}
              onChange={e => setDetails(prev => ({ ...prev, project_id: e.target.value }))}
            >
              <option value="">No project (uses the default AIME domain)</option>
              {projects.filter(p => !p.is_archived).map(p => (
                <option key={p.project_id} value={p.project_id}>{p.name}</option>
              ))}
            </select>
            <p className="editor-field-hint">
              Auto-set to your active project. Determines which custom domain (if any is connected)
              this product's shareable link uses instead of aime.clearbyte.au.
            </p>
          </div>

          <div className="editor-form-group">
            <label className="editor-checkbox-label">
              <input
                type="checkbox"
                checked={details.is_lead_magnet}
                onChange={e => setDetails(prev => ({ ...prev, is_lead_magnet: e.target.checked }))}
              />
              {' '}Mark this product as a lead magnet (usable as a landing-page CTA / free download)
            </label>
            <span className="editor-hint">
              This is separate from the product type above — check this to make the product
              selectable as a lead magnet on landing pages, regardless of its type.
              {productStatus !== 'active' && (
                <> The product must also be <strong>published</strong> (status: active) before it
                will appear in the landing-page picker.</>
              )}
            </span>
          </div>

          <div className="editor-form-group">
            <label>Short Description</label>
            <input
              type="text"
              placeholder="One-line summary shown in listings"
              value={details.short_description}
              onChange={e => setDetails(prev => ({ ...prev, short_description: e.target.value }))}
              maxLength={160}
            />
            <span className="editor-hint">{details.short_description.length}/160 characters</span>
          </div>

          <div className="editor-form-group">
            <label>Description</label>
            <textarea
              placeholder="Full product description, what buyers will receive, etc."
              value={details.description}
              onChange={e => setDetails(prev => ({ ...prev, description: e.target.value }))}
              rows={5}
            />
          </div>

          {/* Cover / Thumbnail */}
          <div className="editor-form-group">
            <label>Product Thumbnail</label>
            <div className="cover-upload-area">
              {coverUrl ? (
                <div className="cover-preview-wrap">
                  <img src={coverUrl} alt="Product cover" className="cover-preview-img" />
                  <div className="cover-preview-actions">
                    <button
                      className="cover-change-btn"
                      onClick={() => coverInputRef.current?.click()}
                      disabled={coverUploading || !savedProductId}
                    >
                      {coverUploading ? 'Uploading…' : 'Change'}
                    </button>
                    <button className="cover-remove-btn" onClick={handleCoverRemove}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className={`cover-drop-zone${!savedProductId ? ' cover-disabled' : ''}`}
                  onClick={() => savedProductId && coverInputRef.current?.click()}
                  title={savedProductId ? 'Click to upload cover image' : 'Save details first to upload a cover'}
                >
                  {coverUploading ? (
                    <span className="cover-uploading-label">Uploading…</span>
                  ) : (
                    <>
                      <ImageIcon size={28} />
                      <span>{savedProductId ? 'Click to upload thumbnail' : 'Save details first, then add a thumbnail'}</span>
                      <span className="upload-hint">JPEG, PNG or WebP · max 5 MB · saved at 800×600</span>
                    </>
                  )}
                </div>
              )}
              <input
                ref={coverInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={handleCoverUpload}
              />
            </div>
            {coverError && <p className="editor-field-error">{coverError}</p>}
          </div>

          <div className="editor-nav-buttons">
            <div />
            <GlassButton
              variant="primary"
              icon={<ArrowRight size={16} />}
              iconPosition="right"
              onClick={handleNext}
              loading={detailsSaving}
            >
              Save &amp; Continue
            </GlassButton>
          </div>
        </GlassCard>
      )}

      {/* ---- Step 1: Files ---- */}
      {currentStep === 1 && (
        <GlassCard variant="default" padding="lg" className="editor-step-panel">
          <h2>Product Files</h2>

          {/* Video products: show Bunny uploader instead of file list */}
          {details.product_type === 'video' ? (
            <>
              {savedProductId ? (
                <VideoUploader productId={savedProductId} />
              ) : (
                <div className="products-error">Save product details first before uploading a video.</div>
              )}
              <div className="upload-note">
                <FileText size={14} />
                Videos are streamed securely via Bunny.net CDN. Configure your Bunny account in the Video Settings tab.
              </div>
            </>
          ) : (
            <>
              {filesError && <div className="products-error">{filesError}</div>}

              <div
                className={`upload-drop-zone${dragOver ? ' drag-over' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleFileDrop}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
              >
                <UploadCloud size={36} />
                <p>Drop files here or click to browse</p>
                <span className="upload-hint">PDF, ZIP, MP4, images and more supported</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileInput}
                />
              </div>

              {files.length > 0 && (
                <div className="file-list">
                  {files.map((file, index) => (
                    <div key={index} className="file-item">
                      <div className="file-item-icon">
                        <FileText size={20} />
                      </div>
                      <div className="file-item-info">
                        <div className="file-item-name">{file.name}</div>
                        <div className="file-item-size">
                          {formatFileSize(file.size)}
                          {file.uploaded && (
                            <span style={{ marginLeft: '8px', color: 'var(--success)' }}>Uploaded</span>
                          )}
                        </div>
                      </div>
                      <span className="file-type-badge">{getFileExt(file.name)}</span>
                      <GlassButton
                        variant="ghost"
                        size="sm"
                        icon={<Trash2 size={14} />}
                        onClick={() => removeFile(index)}
                        title="Remove file"
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="upload-note">
                <FileText size={14} />
                {details.product_type === 'physical'
                  ? 'Physical products do not require uploaded files. The vendor is responsible for fulfilment and shipping.'
                  : 'These files will be securely delivered to buyers after purchase via a time-limited download link.'}
              </div>
            </>
          )}

          <div className="editor-nav-buttons">
            <GlassButton variant="secondary" icon={<ArrowLeft size={16} />} onClick={handleBack}>
              Back
            </GlassButton>
            <GlassButton
              variant="primary"
              icon={<ArrowRight size={16} />}
              iconPosition="right"
              onClick={handleNext}
              loading={uploading}
            >
              {uploading ? 'Uploading...' : 'Save & Continue'}
            </GlassButton>
          </div>
        </GlassCard>
      )}

      {/* ---- Step 2: Pricing ---- */}
      {currentStep === 2 && (
        <GlassCard variant="default" padding="lg" className="editor-step-panel">
          <h2>Pricing</h2>

          {variantsError && <div className="products-error">{variantsError}</div>}

          {details.product_type === 'lead_magnet' ? (
            <div className="products-success">
              This product is a Free Lead Magnet — no pricing required. Buyers download it at no charge.
            </div>
          ) : (
            <>
              <div className="variant-table-wrapper">
                <table className="variant-table">
                  <thead>
                    <tr>
                      <th>Label</th>
                      <th>Price</th>
                      <th>Currency</th>
                      <th>Billing</th>
                      <th>Stock limit</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {variants.map((variant, index) => (
                      <tr key={index}>
                        <td>
                          <input
                            type="text"
                            placeholder="e.g. Standard"
                            value={variant.label}
                            onChange={e => updateVariant(index, 'label', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="9.99"
                            value={variant.price}
                            onChange={e => updateVariant(index, 'price', e.target.value)}
                          />
                        </td>
                        <td>
                          <select
                            value={variant.currency}
                            onChange={e => updateVariant(index, 'currency', e.target.value)}
                          >
                            <option value="USD">USD</option>
                            <option value="GBP">GBP</option>
                            <option value="EUR">EUR</option>
                            <option value="AUD">AUD</option>
                          </select>
                        </td>
                        <td>
                          <select
                            value={variant.billing_type}
                            onChange={e => updateVariant(index, 'billing_type', e.target.value)}
                          >
                            <option value="one_time">One-time</option>
                            <option value="monthly">Monthly</option>
                            <option value="annual">Annual</option>
                          </select>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <input
                              type="number"
                              min="1"
                              step="1"
                              placeholder="Unlimited"
                              value={variant.stock_quantity}
                              onChange={e => updateVariant(index, 'stock_quantity', e.target.value)}
                              style={{ width: '90px' }}
                            />
                            {variant.stock_sold != null && variant.stock_sold > 0 && (
                              <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                                {variant.stock_sold} sold
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <GlassButton
                            variant="ghost"
                            size="sm"
                            icon={<Trash2 size={14} />}
                            onClick={() => removeVariant(index)}
                            disabled={variants.length === 1}
                            title="Remove variant"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: '12px' }}>
                <GlassButton
                  variant="secondary"
                  size="sm"
                  icon={<Plus size={14} />}
                  onClick={addVariant}
                >
                  Add Variant
                </GlassButton>
              </div>
            </>
          )}

          <div className="editor-nav-buttons">
            <GlassButton variant="secondary" icon={<ArrowLeft size={16} />} onClick={handleBack}>
              Back
            </GlassButton>
            <GlassButton
              variant="primary"
              icon={<ArrowRight size={16} />}
              iconPosition="right"
              onClick={handleNext}
              loading={variantsSaving}
            >
              Save &amp; Continue
            </GlassButton>
          </div>
        </GlassCard>
      )}

      {/* ---- Step 3: Publish ---- */}
      {currentStep === 3 && (
        <GlassCard variant="default" padding="lg" className="editor-step-panel">
          <h2>Publish</h2>

          {isLive && (
            <div className="product-live-banner">
              <CheckCircle size={18} />
              Product is live — customers can now purchase it.
            </div>
          )}

          <div className="publish-checklist">
            {checklistItems.map((item, i) => (
              <div key={i} className={`checklist-item${item.pass ? ' pass' : ' fail'}`}>
                <span className={`checklist-icon${item.pass ? ' pass' : ' fail'}`}>
                  {item.pass ? <CheckCircle size={18} /> : <XCircle size={18} />}
                </span>
                <span className="checklist-text">{item.label}</span>
                {item.hint && (
                  <span className="checklist-action-hint">{item.hint}</span>
                )}
              </div>
            ))}
          </div>

          {publishError && <div className="products-error">{publishError}</div>}

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {!isLive ? (
              <GlassButton
                variant="primary"
                size="lg"
                onClick={handlePublish}
                loading={publishing}
                disabled={!publishReady}
              >
                Publish Product
              </GlassButton>
            ) : (
              <GlassButton
                variant="secondary"
                onClick={handleUnpublish}
              >
                Unpublish
              </GlassButton>
            )}
          </div>

          <div style={{ marginTop: '20px' }}>
            <GlassButton variant="secondary" icon={<ArrowLeft size={16} />} onClick={handleBack}>
              Back
            </GlassButton>
          </div>
        </GlassCard>
      )}
    </div>
  );
};

export default ProductEditor;
